<template>
  <v-btn v-if="displayReview" icon size="x-small" v-bind="$attrs">
    <v-icon size="large" :color="starColor">fas fa-star</v-icon>
    <v-dialog v-model="reviewWindowIsOpened" activator="parent" width="auto" min-width="500" max-width="600" opacity="0.6">
      <v-card v-if="! slideName">
        <div class="pa-6 text-center">
          <div class="text-h6 font-weight-bold">{{ $t('REVIEW.DO_YOU_LIKE') }}</div>
          <div class="line-height-normal my-2 mx-auto" style="max-width: 450px">{{ $t('REVIEW.DESC') }}</div>
          <div class="my-2 d-inline-flex position-relative">
            <v-rating v-model="rating.global" hover :color="starColor"></v-rating>
            <div style="width: 100px"></div>
            <!-- workaround because otherwise v-rating hover is active over the button -->
            <div class="d-flex align-center" style="position: absolute; right: 0; top: 0; bottom: 0; width: 100px">
              <v-btn color="primary" size="small" rounded class="ml-4 px-6" @click="sendRating">{{ $t('REVIEW.SEND') }}</v-btn>
            </div>
          </div>
          <div class="my-12"><img src="./img/review1.svg" style="width: 100%"></div>

          <div class="d-flex justify-center gap-4">
            <v-btn variant="text" color="medium-emphasis" @click="ignore()">{{ $t('REVIEW.IGNORE') }}</v-btn>
            <v-btn variant="text" color="medium-emphasis" @click="remindLater()">{{ $t('REVIEW.LATER') }}</v-btn>
          </div>
        </div>
      </v-card>

      <v-card v-if="slideName == 'survey'">
        <div class="pa-6 text-center">
          <div class="text-h6 font-weight-bold">{{ $t('REVIEW.THANKS_FOR_NOTATION') }}</div>
          <div class="line-height-normal my-2 mx-auto" style="max-width: 500px">{{ $t('REVIEW.SURVEY_DESC') }}</div>
          <table class="mt-6 mx-auto text-left">
            <tbody>
              <tr v-for="(textKey, item) in { usability: 'SURVEY_USABILITY', design: 'SURVEY_DESIGN', support: 'SURVEY_SUPPORT', features: 'SURVEY_FEATURES' }">
                <td class="py-2 pr-3">{{ $t(`REVIEW.${textKey}`) }}</td>
                <td><v-rating v-model="rating[item]" hover :color="starColor" style="vertical-align: middle"></v-rating></td>
              </tr>
            </tbody>
          </table>
          <v-textarea v-model="rating.comment" :placeholder="$t('REVIEW.COMMENT')" rows="3" class="mt-3 mb-4"></v-textarea>
          <div>
            <v-btn color="primary" rounded class="px-6" @click="sendSurvey">{{ $t('REVIEW.SEND') }}</v-btn>
          </div>
        </div>
        <div class="pa-6 text-center">
          <div class="d-flex justify-center gap-4">
            <v-btn variant="text" color="medium-emphasis" @click="ignore()">{{ $t('REVIEW.IGNORE') }}</v-btn>
            <v-btn variant="text" color="medium-emphasis" @click="remindLater()">{{ $t('REVIEW.LATER') }}</v-btn>
          </div>
        </div>
      </v-card>

      <v-card v-if="slideName == 'external-review'">
        <div class="pa-6 text-center">
          <v-rating :model-value="5" :color="starColor" readonly></v-rating>
          <div class="text-h6 font-weight-bold">{{ $t('REVIEW.THANKS_FOR_NOTATION') }}</div>
          <div class="line-height-normal mt-1 mb-6 mx-auto" style="max-width: 500px">{{ $t('REVIEW.EXTERNAL_DESC') }}</div>
          <div class="flex-center-center gap-8 line-height-normal">
            <div v-for="platform in reviewPlatforms" style="flex-basis: 100px">
              <v-btn icon color="background" :href="platform.url" target="_blank" rel="noopener noreferrer" @click="clickedOnPlatform = true">
                <img :src="platform.img">
              </v-btn>
              <div class="mt-2">
                <a :href="platform.url" target="_blank" rel="noopener noreferrer" style="color: inherit" @click="clickedOnPlatform = true">{{ platform.name }}</a>
              </div>
            </div>
          </div>
          <div v-if="clickedOnPlatform" class="mt-8">
            <v-btn color="primary" rounded class="px-6" @click="didReview()">{{ $t('REVIEW.DID_REVIEW') }}</v-btn>
          </div>
        </div>
        <div class="position-relative overflow-hidden" style="min-height: 320px; color: rgba(0, 0, 0, 0.87) !important">
          <img src="./img/review-mug.jpg" style="max-width: 100%; position: relative; top: 8px">
          <div class="small line-height-normal text-center" style="position: absolute; top: 24px; left: 0; right: 0; max-width: 400px; margin: auto">
            <span v-html="$t('REVIEW.GIFT')"></span>
          </div>
          <div class="d-flex justify-center gap-4 mb-1" style="position: absolute; bottom: 0; left: 0; right: 0">
            <v-btn variant="text" color="medium-emphasis" @click="ignore()">{{ $t('REVIEW.IGNORE') }}</v-btn>
            <v-btn variant="text" color="medium-emphasis" @click="remindLater()">{{ $t('REVIEW.LATER') }}</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
    <v-tooltip location="bottom" activator="parent">{{ $t('REVIEW.GIVE_REVIEW') }}</v-tooltip>
  </v-btn>

  <v-snackbar v-model="displayThanksMessage" color="surface" close-on-content-click>
    <div class="flex-center-center"><v-icon color="successgreen" start>fas fa-check-circle</v-icon>{{ $t('REVIEW.THANKS_FOR_REVIEW') }}</div>
  </v-snackbar>
</template>

<script>
  import { mapState, mapActions } from 'pinia';
  import useUserOptionsStore from '@/stores/user-options';
  import useUiNavigationStore from '@/stores/ui/navigation';
  import useUsersStore from '@/stores/users';
  import useUsersAccessRightStore from '@/stores/users/accessRight';
  import googleImg from './img/google.png';
  import capterraImg from './img/capterra.png';
  import trustpilotImg from './img/trustpilot.png';

  export default {
    data() {
      return {
        displayReview: false,
        starColor: '#FFA500',
        slideName: '',
        rating: {
          global: 0,
          usability: 0,
          design: 0,
          support: 0,
          features: 0,
          comment: '',
        },
        reviewPlatforms: [
          { name: 'Google', url: 'https://g.page/r/CSvhpIYmxDx6EAE/review', img: googleImg },
          { name: 'Capterra', url: 'https://reviews.capterra.com/products/new/a7833f2e-3a04-4b01-8ade-a9080087631c/?lang=fr', img: capterraImg },
          { name: 'Trustpilot', url: 'https://fr.trustpilot.com/evaluate/bubbleplan.net', img: trustpilotImg },
        ],
        clickedOnPlatform: false,
        displayThanksMessage: false,
        uiNavigationStore: useUiNavigationStore(),
        usersStore: useUsersStore(),
        usersAccessRightStore: useUsersAccessRightStore(),
      };
    },
    computed: {
      reviewWindowIsOpened: {
        get() {
          return this.uiNavigationStore.openedModal == 'review-window';
        },
        set(newVal) {
          this.uiNavigationStore.openedModal = (newVal ? 'review-window' : null);
        },
      },
      ...mapState(useUserOptionsStore, ['userOptions']),
    },
    created() {
      this.usersStore.userPromise.then((user) => {
        const acr = this.usersAccessRightStore;
        if (! acr.isPremium || acr.isTrial || acr.isConsultOnly || moment(user.created_at).isAfter(moment().subtract(1, 'year'))) return;
        const premiumExpireDate = user.company?.premiumExpireDate && moment(user.company.premiumExpireDate);
        if (! premiumExpireDate?.isValid() || premiumExpireDate.isAfter(moment().add(10, 'months')) || premiumExpireDate.isBefore(moment().add(3, 'months'))) return;

        const alreadyLoaded = !! this.userOptions.review; // already loaded from a previous screen
        const userOptionPromise = alreadyLoaded ? Promise.resolve({ review: this.userOptions.review }) : this.loadUserOptions({ key: 'review' });
        userOptionPromise.then(({ review }) => {
          this.userOptions.review = review || {};
          const { status, updated_at, rating } = this.userOptions.review;
          if (status == 'done') return;
          if (status == 'ignore' && moment(updated_at).isAfter(moment().subtract(6, 'months'))) return;
          _.extend(this.rating, rating || {});
          this.displayReview = true;
          if (status == 'later' && moment(updated_at).isAfter(moment().startOf('day').subtract(3, 'days'))) return; // show button only
          if (status == 'pending' && rating?.global) this.slideName = rating.global == 5 ? 'external-review' : 'survey';
          if (! alreadyLoaded) this.reviewWindowIsOpened = true;
        }).catch(() => {});
      });
    },
    methods: {
      saveUserReview() {
        this.userOptions.review.updated_at = moment().format();
        this.saveUserOption({ key: 'review', value: this.userOptions.review });
      },
      close({ ignore = false } = {}) {
        if (ignore) this.displayReview = false;
        this.uiNavigationStore.openedModal = null;
      },
      ignore() {
        this.userOptions.review.status = 'ignore';
        this.saveUserReview();
        this.close({ ignore: true });
      },
      remindLater() {
        this.userOptions.review.status = 'later';
        this.saveUserReview();
        this.close();
      },
      sendRating() {
        if (! this.rating.global) return;

        if (! this.userOptions.review.rating) this.userOptions.review.rating = {};
        this.userOptions.review.rating.global = this.rating.global;
        this.userOptions.review.status = 'pending';
        this.saveUserReview();

        if (this.rating.global == 5) {
          this.slideName = 'external-review';
        } else {
          this.slideName = 'survey';
        }
      },
      sendSurvey() {
        const props = ['usability', 'design', 'support', 'features'];
        if (props.some(prop => ! this.rating[prop])) return;
        props.push('comment'); // optional field
        props.forEach((prop) => { this.userOptions.review.rating[prop] = this.rating[prop]; });
        this.userOptions.review.status = 'done';
        this.saveUserReview();
        this.close({ ignore: true });
        this.displayThanksMessage = true;
      },
      didReview() {
        this.userOptions.review.status = 'done';
        this.saveUserReview();
        this.close({ ignore: true });
        this.displayThanksMessage = true;
      },
      ...mapActions(useUserOptionsStore, ['loadUserOptions', 'saveUserOption']),
    },
  };
</script>
